import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Heart, Baby, TrendingUp, Activity, Calendar, AlertCircle, CheckCircle, Search } from 'lucide-react';

const HealthTracking = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('antenatal');
  const [searchTerm, setSearchTerm] = useState('');
  const pregnancyData = [
    {
      patient: 'Mrs. Ngozi Okeke',
      gestationalAge: 27,
      bp: '120/80',
      weight: 68,
      fundalHeight: 27,
      status: 'normal',
      lastUpdate: '2026-01-18',
    },
    {
      patient: 'Mrs. Adaobi Nwankwo',
      gestationalAge: 30,
      bp: '115/75',
      weight: 62,
      fundalHeight: 26,
      status: 'attention',
      lastUpdate: '2026-01-10',
    },
    {
      patient: 'Mrs. Chidinma Eze',
      gestationalAge: 35,
      bp: '118/78',
      weight: 58,
      fundalHeight: 39,
      status: 'attention',
      lastUpdate: '2025-12-20',
    },
  ];

  // Function to check if fundal height is abnormal
  const isFundalHeightAbnormal = (fundalHeight, gestationalAge) => {
    if (fundalHeight < 24 || fundalHeight > 38) return true;
    const difference = Math.abs(fundalHeight - gestationalAge);
    return difference >= 3;
  };

  const childHealthData = [
    {
      patient: 'Baby Chiamaka (Mrs. Aisha)',
      age: '18 months',
      weight: '11.2 kg',
      height: '80 cm',
      vaccinations: 'Up to date',
      status: 'healthy',
      lastUpdate: '2026-01-05',
    },
  ];

  const postnatalData = [
    {
      patient: 'Mrs. Fatima Bello',
      weeksPostpartum: 6,
      bp: '122/82',
      uterineFundus: 'Well contracted',
      weight: 65,
      moodScreening: 'Normal',
      cordCare: 'Healing well',
      status: 'recovering',
      lastUpdate: '2026-01-15',
    },
    {
      patient: 'Mrs. Grace Okonkwo',
      weeksPostpartum: 2,
      bp: '118/76',
      uterineFundus: 'Normal involution',
      weight: 62,
      moodScreening: 'Mild anxiety',
      cordCare: 'Clean and dry',
      status: 'normal',
      lastUpdate: '2026-01-20',
    },
  ];

  return (
    <div className="p-6 md:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl shadow-lg shadow-pink-500/30">
              <Activity className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                {t('healthTrackingTitle')}
              </h1>
              <p className="text-gray-600 mt-1">{t('healthTrackingSubtitle')}</p>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search patients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-2 mb-6 flex gap-2 overflow-x-auto">
          <button
            onClick={() => setActiveTab('antenatal')}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'antenatal'
                ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/30'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Heart className="w-5 h-5" />
            Antenatal Care
          </button>
          <button
            onClick={() => setActiveTab('postnatal')}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'postnatal'
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg shadow-purple-500/30'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <TrendingUp className="w-5 h-5" />
            Postnatal Care
          </button>
          <button
            onClick={() => setActiveTab('child')}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'child'
                ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg shadow-emerald-500/30'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Baby className="w-5 h-5" />
            Child Health
          </button>
        </div>

        {/* Antenatal Tracking */}
        {activeTab === 'antenatal' && (
          <div className="space-y-4">
            {pregnancyData
              .filter(p => p.patient.toLowerCase().includes(searchTerm.toLowerCase()))
              .map((record, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{record.patient}</h3>
                        <p className="text-gray-600 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Gestational Age: {record.gestationalAge} weeks
                        </p>
                      </div>
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                        record.status === 'normal' ? 'bg-green-100 text-green-700' : 
                        record.status === 'attention' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {record.status === 'normal' && <CheckCircle className="w-4 h-4 inline mr-1" />}
                        {record.status === 'attention' && <AlertCircle className="w-4 h-4 inline mr-1" />}
                        {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-4 rounded-xl border border-pink-100">
                        <p className="text-sm text-gray-600 mb-1">Blood Pressure</p>
                        <p className="text-lg font-bold text-gray-900">{record.bp}</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-sky-50 p-4 rounded-xl border border-blue-100">
                        <p className="text-sm text-gray-600 mb-1">Weight</p>
                        <p className="text-lg font-bold text-gray-900">{record.weight} kg</p>
                      </div>
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-xl border border-amber-100">
                        <p className="text-sm text-gray-600 mb-1">Gestational Age</p>
                        <p className="text-lg font-bold text-gray-900">{record.gestationalAge} weeks</p>
                      </div>
                      <div className={`p-4 rounded-xl border ${
                        isFundalHeightAbnormal(record.fundalHeight, record.gestationalAge)
                          ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-300'
                          : 'bg-gradient-to-br from-purple-50 to-purple-50 border-purple-100'
                      }`}>
                        <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                          Fundal Height
                          {isFundalHeightAbnormal(record.fundalHeight, record.gestationalAge) && (
                            <AlertCircle className="w-3 h-3 text-red-600" />
                          )}
                        </p>
                        <p className={`text-lg font-bold ${
                          isFundalHeightAbnormal(record.fundalHeight, record.gestationalAge)
                            ? 'text-red-700'
                            : 'text-gray-900'
                        }`}>
                          {record.fundalHeight} cm
                        </p>
                        {isFundalHeightAbnormal(record.fundalHeight, record.gestationalAge) && (
                          <p className="text-xs text-red-600 mt-1">
                            {Math.abs(record.fundalHeight - record.gestationalAge) >= 3
                              ? `±${Math.abs(record.fundalHeight - record.gestationalAge)}cm variance`
                              : 'Out of range'}
                          </p>
                        )}
                      </div>
                      <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-4 rounded-xl border border-emerald-100">
                        <p className="text-sm text-gray-600 mb-1">Last Update</p>
                        <p className="text-lg font-bold text-gray-900">
                          {new Date(record.lastUpdate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Postnatal Tracking */}
        {activeTab === 'postnatal' && (
          <div className="space-y-4">
            {postnatalData
              .filter(p => p.patient.toLowerCase().includes(searchTerm.toLowerCase()))
              .map((record, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{record.patient}</h3>
                        <p className="text-gray-600 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {record.weeksPostpartum} weeks postpartum
                        </p>
                      </div>
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                        record.status === 'recovering' ? 'bg-yellow-100 text-yellow-700' : 
                        record.status === 'normal' ? 'bg-green-100 text-green-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-4 rounded-xl border border-pink-100">
                        <p className="text-sm text-gray-600 mb-1">Blood Pressure</p>
                        <p className="text-lg font-bold text-gray-900">{record.bp}</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-purple-50 p-4 rounded-xl border border-purple-100">
                        <p className="text-sm text-gray-600 mb-1">Uterine Fundus</p>
                        <p className="text-lg font-bold text-gray-900">{record.uterineFundus}</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-sky-50 p-4 rounded-xl border border-blue-100">
                        <p className="text-sm text-gray-600 mb-1">Weight</p>
                        <p className="text-lg font-bold text-gray-900">{record.weight} kg</p>
                      </div>
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-xl border border-amber-100">
                        <p className="text-sm text-gray-600 mb-1">Mood Screening</p>
                        <p className="text-lg font-bold text-gray-900">{record.moodScreening}</p>
                      </div>
                      <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-4 rounded-xl border border-emerald-100">
                        <p className="text-sm text-gray-600 mb-1">Cord Care</p>
                        <p className="text-lg font-bold text-gray-900">{record.cordCare}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Child Health Tracking */}
        {activeTab === 'child' && (
          <div className="space-y-4">
            {childHealthData
              .filter(p => p.patient.toLowerCase().includes(searchTerm.toLowerCase()))
              .map((record, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{record.patient}</h3>
                        <p className="text-gray-600 flex items-center gap-2">
                          <Baby className="w-4 h-4" />
                          Age: {record.age}
                        </p>
                      </div>
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                        record.status === 'healthy' ? 'bg-green-100 text-green-700' : 
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-4 rounded-xl border border-pink-100">
                        <p className="text-sm text-gray-600 mb-1">Weight</p>
                        <p className="text-lg font-bold text-gray-900">{record.weight}</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-sky-50 p-4 rounded-xl border border-blue-100">
                        <p className="text-sm text-gray-600 mb-1">Height</p>
                        <p className="text-lg font-bold text-gray-900">{record.height}</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-purple-50 p-4 rounded-xl border border-purple-100">
                        <p className="text-sm text-gray-600 mb-1">Vaccinations</p>
                        <p className="text-lg font-bold text-gray-900">{record.vaccinations}</p>
                      </div>
                      <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-4 rounded-xl border border-emerald-100">
                        <p className="text-sm text-gray-600 mb-1">Last Update</p>
                        <p className="text-lg font-bold text-gray-900">
                          {new Date(record.lastUpdate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthTracking;
